/**
 * Revenue Forecast - Grouped by Account Manager > Client > Project
 * Excel-like interface for project revenue forecasting
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
  UploadFile as UploadIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  FactCheck as FactCheckIcon,
  CloudSync as CloudSyncIcon,
} from '@mui/icons-material';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/apiConfig';
import { bevestig } from '../../utils/bevestig';
import PlanningCheck, { PlanningCheckDetail } from './PlanningCheck';
import { authFetch } from '../../utils/authHelpers';
import PageHeader from '../../ui-components/PageHeader';

// Compact currency format
const fmt = (n) => n ? `€${Math.round(n).toLocaleString('nl-NL')}` : '-';

// Generate months: 3 months back + 12 months forward = 15 months total
const generateMonths = () => {
  const months = [];
  const now = new Date();
  for (let i = -3; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    months.push({
      key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleDateString('nl-NL', { month: 'short' }).substring(0, 3),
      year: date.getFullYear().toString().slice(-2),
      isPast: i < 0
    });
  }
  return months;
};

// Editable cell
const Cell = ({ value, onChange, disabled, type = 'percent' }) => {
  const [val, setVal] = useState(value || '');
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setVal(value || ''); }, [value]);

  const commit = () => {
    const num = parseFloat(val) || 0;
    if (num !== value) onChange(num);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      commit();
      if (e.key === 'Enter') {
        e.preventDefault();
        const next = ref.current?.closest('td')?.nextElementSibling?.querySelector('input');
        if (next) next.focus();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const td = ref.current?.closest('td');
      const cellIndex = td?.cellIndex;
      const nextRow = td?.closest('tr')?.nextElementSibling;
      const nextCell = nextRow?.cells[cellIndex]?.querySelector('input');
      if (nextCell) nextCell.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const td = ref.current?.closest('td');
      const cellIndex = td?.cellIndex;
      const prevRow = td?.closest('tr')?.previousElementSibling;
      const prevCell = prevRow?.cells[cellIndex]?.querySelector('input');
      if (prevCell) prevCell.focus();
    }
  };

  const hasVal = val > 0;
  const isPercent = type === 'percent';

  return (
    <input
      ref={ref}
      type="number"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onFocus={(e) => { setFocused(true); e.target.select(); }}
      onBlur={() => { setFocused(false); commit(); }}
      onKeyDown={onKey}
      disabled={disabled}
      placeholder="-"
      style={{
        width: isPercent ? 50 : 70,
        height: 24,
        border: focused ? '2px solid #3b82f6' : '1px solid #d1d5db',
        borderRadius: 0,
        textAlign: 'right',
        padding: '2px 4px',
        fontSize: 12,
        fontFamily: 'Inter, system-ui, sans-serif',
        fontWeight: hasVal ? 600 : 400,
        backgroundColor: hasVal ? '#eff6ff' : '#fff',
        outline: 'none',
        boxSizing: 'border-box'
      }}
    />
  );
};

// Calculate monthly BBI totals for a set of rows
const calcMonthlyTotals = (rows, months) => {
  const mt = {};
  months.forEach(m => { mt[m.key] = 0; });
  rows.forEach(r => {
    months.forEach(m => {
      const p = (r.pct || {})[m.key] || 0;
      mt[m.key] += ((r.omzet || 0) - (r.costs || 0)) * p / 100;
    });
  });
  return mt;
};

// Main Component
export default function RevenueForecast() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const activeAccount = instance.getActiveAccount();
  const userEmail = activeAccount?.username || '';
  const [allProjects, setAllProjects] = useState([]);
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [forecastData, setForecastData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPL, setSelectedPL] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [hideInternal, setHideInternal] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Planning-check per projectnummer: taken vs offerteregels per fase + voorstel maandverdeling
  const [planningChecks, setPlanningChecks] = useState({});
  const [planningLaden, setPlanningLaden] = useState(false);
  const [openPlanning, setOpenPlanning] = useState(() => new Set());   // projecten met uitgeklapte planningrij
  const togglePlanning = useCallback((id) => setOpenPlanning((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }), []);
  const [editProject, setEditProject] = useState(null);
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [hideOld, setHideOld] = useState(false);
  const [dialogPL, setDialogPL] = useState('');
  const [dialogOmzet, setDialogOmzet] = useState(0);
  const [dialogCosts, setDialogCosts] = useState(0);
  const fileInputRef = useRef(null);

  const saveTimeoutRef = useRef({});
  const pendingSaves = useRef({});
  const months = useMemo(() => generateMonths(), []);

  // Fetch all projects ONCE on mount
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/forecast-projects`);
      if (!res.ok) throw new Error('Laden mislukt');
      const data = await res.json();
      setAllProjects(data.projects || []);
      setActiveEmployees(data.activeEmployees || []);

      // Fetch saved forecasts
      try {
        const fRes = await authFetch(`${BACKEND_URL}/Api/forecasts`);
        if (fRes.ok) {
          const fData = await fRes.json();
          const map = {};
          (fData.forecasts || []).forEach(f => {
            const key = f.project_number || f.airtable_project_id || f.gripp_project_id;
            map[key] = {
              percentages: f.monthlyPercentages || {},
              costs: f.purchase_costs || 0,
              projectValue: f.project_value || null
            };
          });
          setForecastData(map);
        }
      } catch (e) {
        console.log('Forecasts API not available, using local state only');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // Sync dialog form fields when editProject changes
  useEffect(() => {
    if (editProject) {
      setDialogPL(editProject.pl || '');
      setDialogOmzet(editProject.omzet || 0);
      setDialogCosts(editProject.costs || 0);
    }
  }, [editProject]);

  const plList = useMemo(() => activeEmployees, [activeEmployees]);

  // Filter projects client-side
  const projects = useMemo(() => {
    let filtered = allProjects;
    if (hideInternal) filtered = filtered.filter(p => !p.isInternal);
    if (selectedPL) {
      filtered = filtered.filter(p =>
        p.pl && p.pl.toLowerCase().includes(selectedPL.toLowerCase())
      );
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        String(p.projectnr).includes(q) ||
        (p.name || '').toLowerCase().includes(q) ||
        (p.klant || '').toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [allProjects, selectedPL, searchQuery, hideInternal]);

  // Debounced save
  const save = useCallback((projectId, updates) => {
    const project = allProjects.find(p => p.id === projectId);
    if (!project) return;

    const current = forecastData[projectId] || pendingSaves.current[projectId] || { percentages: {}, costs: 0, projectValue: null };
    const newPct = updates.monthlyPercentages ?? current.percentages;
    const newCosts = updates.purchaseCosts ?? current.costs;
    const newValue = updates.projectValue !== undefined ? updates.projectValue : current.projectValue;

    pendingSaves.current[projectId] = { percentages: newPct, costs: newCosts, projectValue: newValue, project };
    setForecastData(prev => ({ ...prev, [projectId]: { percentages: newPct, costs: newCosts, projectValue: newValue } }));

    if (saveTimeoutRef.current[projectId]) clearTimeout(saveTimeoutRef.current[projectId]);

    saveTimeoutRef.current[projectId] = setTimeout(async () => {
      const d = pendingSaves.current[projectId];
      if (!d) return;
      try {
        await authFetch(`${BACKEND_URL}/Api/forecasts/${projectId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            project_number: d.project.projectnr,
            gripp_project_id: String(d.project.gripp_id || d.project.projectnr),
            project_name: d.project.name,
            company_name: d.project.klant,
            pl: d.project.pl,
            project_value: d.projectValue !== null ? d.projectValue : (d.project.omzet || 0),
            monthlyPercentages: d.percentages,
            purchaseCosts: d.costs
          })
        });
        delete pendingSaves.current[projectId];
      } catch (err) {
        console.error('Save error:', err);
      }
    }, 500);
  }, [allProjects, forecastData]);

  // Build rows with calculations + apply post-compute filters
  const rows = useMemo(() => {
    let result = projects.map(p => {
      const fd = forecastData[p.id] || { percentages: {}, costs: 0, projectValue: null };
      const pct = fd.percentages || {};
      const totalPct = Object.values(pct).reduce((s, v) => s + (v || 0), 0);
      const omzet = fd.projectValue !== null && fd.projectValue !== undefined ? fd.projectValue : (p.omzet || 0);
      return {
        ...p,
        pct,
        costs: fd.costs || 0,
        totalPct,
        omzet,
        originalOmzet: p.omzet || 0,
        bbi: omzet - (fd.costs || 0)
      };
    });

    // Filter: show only projects with incomplete percentages (0 < totalPct < 100)
    if (showIncomplete) {
      result = result.filter(r => r.totalPct > 0 && r.totalPct !== 100);
    }

    // Filter: hide projects where all percentages are in past months AND totalPct === 100,
    // plus projecten die in Gripp al gearchiveerd/afgerond/geannuleerd zijn (die zijn geen prognose meer)
    if (hideOld) {
      result = result.filter(r => {
        if (r.isActive === false || ['Gearchiveerd', 'Afgerond', 'Geannuleerd', 'Vernietigd'].includes(r.status)) return false;
        if (r.totalPct !== 100) return true;
        const hasNonPastPct = months.some(m => !m.isPast && (r.pct[m.key] || 0) > 0);
        return hasNonPastPct;
      });
    }

    return result;
  }, [projects, forecastData, showIncomplete, hideOld, months]);

  // Group by Account Manager (PL) → Client
  const grouped = useMemo(() => {
    const plGroups = {};
    rows.forEach(r => {
      const pl = r.pl || 'Geen CSD';
      const klant = r.klant || 'Onbekend';
      if (!plGroups[pl]) plGroups[pl] = {};
      if (!plGroups[pl][klant]) plGroups[pl][klant] = [];
      plGroups[pl][klant].push(r);
    });

    // Sort PLs alphabetically, then clients alphabetically
    const sorted = Object.entries(plGroups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([pl, clients]) => ({
        pl,
        clients: Object.entries(clients)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([klant, projects]) => ({
            klant,
            projects: projects.sort((a, b) => String(a.projectnr).localeCompare(String(b.projectnr)))
          })),
        allRows: Object.values(clients).flat()
      }));
    return sorted;
  }, [rows]);

  // Grand totals
  const totals = useMemo(() => {
    const t = { omzet: 0, costs: 0, bbi: 0, monthly: {} };
    months.forEach(m => { t.monthly[m.key] = 0; });
    rows.forEach(r => {
      t.omzet += r.omzet || 0;
      t.costs += r.costs || 0;
      t.bbi += r.bbi || 0;
      months.forEach(m => {
        const p = r.pct[m.key] || 0;
        t.monthly[m.key] += ((r.omzet || 0) - (r.costs || 0)) * p / 100;
      });
    });
    return t;
  }, [rows, months]);

  // Toggle group collapse - when collapsing a PL group, also collapse all its client subgroups
  const toggleGroup = (key) => {
    setCollapsedGroups(prev => {
      const isCollapsing = !prev[key];
      const next = { ...prev, [key]: isCollapsing };
      // If collapsing a PL group, also collapse all client subgroups within it
      if (isCollapsing && key.startsWith('pl:')) {
        Object.keys(prev).forEach(k => {
          if (k.startsWith(key + '|client:')) next[k] = true;
        });
        // Also collapse client groups not yet in state
        const plGroup = grouped.find(g => `pl:${g.pl}` === key);
        if (plGroup) {
          plGroup.clients.forEach(c => {
            next[`${key}|client:${c.klant}`] = true;
          });
        }
      }
      return next;
    });
  };

  // Excel import handler
  const handleExcelImport = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // Reset so same file can be re-selected

    setImporting(true);
    setImportResult(null);
    setError(null);

    try {
      // exceljs lazy laden: alleen nodig bij een import, en zo blijft de
      // hoofdbundel klein (vervangt de kwetsbare npm-versie van xlsx).
      const ExcelJS = (await import('exceljs')).default;
      const data = await file.arrayBuffer();
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(data);

      // Try known sheet names, fall back to first sheet
      const ws = wb.worksheets.find(w =>
        w.name.toLowerCase().includes('onderhandenwerk') || w.name.toLowerCase().includes('nieuw')
      ) || wb.worksheets[0];
      // Zelfde vorm als sheet_to_json(header:1): array van rij-arrays (0-based).
      const rows = [];
      ws.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        const vals = row.values || [];
        const arr = [];
        for (let c = 1; c < vals.length; c++) {
          let v = vals[c];
          if (v && typeof v === 'object') {
            if (v.richText) v = v.richText.map((t) => t.text).join('');
            else if (v.result !== undefined) v = v.result;
            else if (v.text !== undefined) v = v.text;
          }
          arr[c - 1] = v === null || v === undefined ? undefined : v;
        }
        rows[rowNumber - 1] = arr;
      });
      for (let i = 0; i < rows.length; i++) if (!rows[i]) rows[i] = [];

      if (rows.length < 2) throw new Error('Geen data gevonden in Excel');

      // Kopregel = de rij met "Project omschrijving" én "BBI" (in het OHW-werkboek rij 11; rij 1 heeft
      // ook maandkoppen maar geen projecten). Daaronder staan de projecten, met de omschrijving in de
      // kolom van "Project omschrijving" (niet per se kolom A).
      const MONTH_NAMES = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
      const cellText = (c) => (c === null || c === undefined ? '' : String(c).trim());
      let headerIdx = rows.findIndex((r) => r && r.some((c) => cellText(c).startsWith('Project omschrijving')) && r.some((c) => cellText(c) === 'BBI'));
      if (headerIdx < 0) headerIdx = 0;
      const header = rows[headerIdx];
      let projectCol = header.findIndex((c) => cellText(c).startsWith('Project omschrijving'));
      if (projectCol < 0) projectCol = 0;
      const bbiCol = header.findIndex((c) => cellText(c) === 'BBI');
      // Kale maandnamen ("Jan") horen bij het jaar vóór de eerste "mmm-JJ"-kop ("jan-26" → 2025).
      const jjKoppen = header.map((c) => cellText(c).toLowerCase().match(/^([a-z]{3})-(\d{2}|\d{4})$/)).filter(Boolean);
      const basisjaar = jjKoppen.length
        ? Math.min(...jjKoppen.map((x) => (x[2].length === 2 ? 2000 + Number(x[2]) : Number(x[2])))) - 1
        : new Date().getFullYear();

      // Detect month columns: headers that match month abbreviations or contain year-month patterns
      const monthCols = {};
      const currentYear = new Date().getFullYear();

      const gezien = new Set();
      header.forEach((h, idx) => {
        if (h === null || h === undefined || idx <= bbiCol) return;
        if (typeof h === 'number') return;                       // eurokolommen rechts hebben getallen als kop
        let key = null;
        if (h instanceof Date) {
          key = `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}`;
        } else {
          const val = String(h).toLowerCase().trim();
          if (!val || val.startsWith('%')) return;
          const iso = val.match(/^(\d{4})-(\d{2})-01/);
          const mmmJJ = val.match(/^([a-z]{3})-(\d{2}|\d{4})$/);
          const mmmJJJJ = val.match(/^([a-z]{3})(\d{4})$/);
          if (iso) key = `${iso[1]}-${iso[2]}`;
          else if (mmmJJ && MONTH_NAMES.includes(mmmJJ[1])) key = `${mmmJJ[2].length === 2 ? 2000 + Number(mmmJJ[2]) : Number(mmmJJ[2])}-${String(MONTH_NAMES.indexOf(mmmJJ[1]) + 1).padStart(2, '0')}`;
          else if (mmmJJJJ && MONTH_NAMES.includes(mmmJJJJ[1])) key = `${mmmJJJJ[2]}-${String(MONTH_NAMES.indexOf(mmmJJJJ[1]) + 1).padStart(2, '0')}`;
          else if (MONTH_NAMES.includes(val)) key = `${basisjaar || currentYear}-${String(MONTH_NAMES.indexOf(val) + 1).padStart(2, '0')}`;
        }
        if (key && !gezien.has(key)) { gezien.add(key); monthCols[idx] = key; }
      });

      if (Object.keys(monthCols).length === 0) {
        throw new Error('Geen maandkolommen gevonden in Excel. Verwacht kolomkoppen als "Jan", "Feb", etc.');
      }

      // Projectregels: "7584. Klantverhaal Alliander" → nummer 7584, naam = hele omschrijving
      const projects = [];
      for (let i = headerIdx + 1; i < rows.length; i++) {
        const row = rows[i];
        if (!row || !row[projectCol]) continue;

        const rawNr = String(row[projectCol]).trim();
        const nrMatch = rawNr.match(/^\d{4,5}\b/);
        if (!nrMatch) continue;
        const projectNumber = nrMatch[0];

        const projectName = rawNr;

        // Extract percentages from month columns
        const percentages = {};
        for (const [colIdx, monthKey] of Object.entries(monthCols)) {
          const val = row[parseInt(colIdx)];
          if (val !== null && val !== undefined && val !== '' && val !== 0) {
            let pct = parseFloat(val);
            if (isNaN(pct)) continue;
            // Werkboek gebruikt breuken (0,3 = 30 %, 1 = 100 %); waarden boven 1,5 zijn al procenten
            if (pct > 0 && pct <= 1.5) pct = pct * 100;
            if (pct > 0) percentages[monthKey] = Math.round(pct * 10) / 10;
          }
        }

        if (Object.keys(percentages).length > 0) {
          projects.push({ project_number: projectNumber, project_name: projectName, percentages });
        }
      }

      if (projects.length === 0) {
        throw new Error('Geen projecten met percentages gevonden');
      }

      // Send to backend
      const res = await authFetch(`${BACKEND_URL}/Api/forecasts/import-from-excel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projects })
      });

      if (!res.ok) throw new Error('Import mislukt');
      const result = await res.json();

      setImportResult(`${result.matched} projecten geïmporteerd${result.not_found?.length ? `, ${result.not_found.length} niet gevonden in Gripp` : ''}`);

      // Refresh data
      fetchProjects();
    } catch (err) {
      setError('Excel import mislukt: ' + err.message);
    } finally {
      setImporting(false);
    }
  }, [fetchProjects]);

  const laadPlanningChecks = useCallback(async (projecten) => {
    const nummers = [...new Set(projecten.map((p) => String(p.projectnr || p.project_number || '').trim()).filter(Boolean))];
    if (!nummers.length) return;
    setPlanningLaden(true);
    try {
      const uit = {};
      for (let i = 0; i < nummers.length; i += 150) {
        const res = await authFetch(`${BACKEND_URL}/Api/forecasts/planning-check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ project_numbers: nummers.slice(i, i + 150) }),
        });
        if (!res.ok) break;
        const d = await res.json();
        Object.assign(uit, d.projecten || {});
      }
      setPlanningChecks(uit);
    } catch (e) {
      console.warn('planning-check niet geladen', e);
    } finally {
      setPlanningLaden(false);
    }
  }, []);

  useEffect(() => {
    if (allProjects.length) laadPlanningChecks(allProjects);
  }, [allProjects, laadPlanningChecks]);

  const handleSharePointImport = useCallback(async () => {
    const ok = await bevestig({
      titel: 'Planning overnemen uit het OHW-bestand?',
      tekst: 'De maandverdeling van projecten die in OnderhandenWerk_FF.xlsx op SharePoint staan wordt overschreven met de verdeling uit dat bestand. Omzet en inkoop blijven staan. Gesloten projecten komen alleen mee als ze al een prognose hadden.',
      knop: 'Overnemen',
    });
    if (!ok) return;
    setImporting(true);
    setImportResult(null);
    setError(null);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/forecasts/import-from-sharepoint?vervang=true`, { method: 'POST' });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result.error || result.detail || `Import mislukt (${res.status})`);
      const wanneer = result.bestand?.gewijzigd ? new Date(result.bestand.gewijzigd).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '';
      setImportResult(`${result.matched} projecten overgenomen uit ${result.bestand?.naam || 'het OHW-bestand'}${wanneer ? ` (bijgewerkt ${wanneer}${result.bestand?.gewijzigd_door ? ` door ${result.bestand.gewijzigd_door}` : ''})` : ''}${result.not_found?.length ? `, ${result.not_found.length} niet gevonden in Gripp` : ''}`);
      fetchProjects();
    } catch (err) {
      setError('Import uit SharePoint mislukt: ' + err.message);
    } finally {
      setImporting(false);
    }
  }, [fetchProjects]);

  if (loading && allProjects.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Styles
  const headerStyle = {
    bgcolor: '#f1f5f9', fontWeight: 700, fontSize: 11, p: '4px 6px',
    borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #cbd5e1', whiteSpace: 'nowrap'
  };

  const stickyCol = {
    position: 'sticky', left: 0, zIndex: 2,
    bgcolor: '#fff', borderRight: '2px solid #cbd5e1'
  };

  const cellStyle = (isEven) => ({
    p: '2px 4px', fontSize: 12,
    borderRight: '1px solid #e5e7eb',
    bgcolor: isEven ? '#fafafa' : '#fff'
  });

  // Total column count: project + omzet + inkoop + bbi + % + months
  const colCount = 5 + months.length;

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <PageHeader
        icon="show_chart"
        title="Revenue Forecast"
        subtitle="Omzetprognose per accountmanager, klant en project"
        rightActions={(
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <TextField
            size="small"
            placeholder="Zoek..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
              sx: { height: 32, fontSize: 13 }
            }}
            sx={{ width: 150 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: 13 }}>Account Manager</InputLabel>
            <Select
              value={selectedPL}
              label="Account Manager"
              onChange={(e) => setSelectedPL(e.target.value)}
              sx={{ height: 32, fontSize: 13 }}
            >
              <MenuItem value="">Alle ({allProjects.filter(p => !hideInternal || !p.isInternal).length})</MenuItem>
              {plList.map(pl => (
                <MenuItem key={pl} value={pl}>{pl}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Verberg interne projecten (Full Frame als klant)">
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideInternal}
                  onChange={(e) => setHideInternal(e.target.checked)}
                  size="small"
                  sx={{ p: 0.5 }}
                />
              }
              label={<Typography sx={{ fontSize: 11 }}>Extern</Typography>}
              sx={{ ml: 1, mr: 0 }}
            />
          </Tooltip>
          <Tooltip title="Toon alleen projecten met onvolledige percentages (niet 0% en niet 100%)">
            <FormControlLabel
              control={
                <Checkbox
                  checked={showIncomplete}
                  onChange={(e) => setShowIncomplete(e.target.checked)}
                  size="small"
                  sx={{ p: 0.5 }}
                />
              }
              label={<Typography sx={{ fontSize: 11 }}>Onvolledig</Typography>}
              sx={{ ml: 1, mr: 0 }}
            />
          </Tooltip>
          <Tooltip title="Verberg projecten die in Gripp gearchiveerd, afgerond of geannuleerd zijn, en projecten waarvan alle percentages in het verleden liggen">
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideOld}
                  onChange={(e) => setHideOld(e.target.checked)}
                  size="small"
                  sx={{ p: 0.5 }}
                />
              }
              label={<Typography sx={{ fontSize: 11 }}>Verberg oud</Typography>}
              sx={{ ml: 1, mr: 0 }}
            />
          </Tooltip>
          <Tooltip title="Planning overnemen uit OnderhandenWerk_FF.xlsx op SharePoint (zelfde bestand als de OHW-stand)">
            <IconButton
              size="small"
              onClick={handleSharePointImport}
              disabled={importing}
              sx={{ color: '#0ea5e9' }}
            >
              {importing ? <CircularProgress size={18} /> : <CloudSyncIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Importeer percentages uit een lokaal Excel-bestand">
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              sx={{ color: '#6366f1' }}
            >
              <UploadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleExcelImport}
            style={{ display: 'none' }}
          />
          <IconButton size="small" onClick={fetchProjects} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
          <Tooltip title="OHW Check — vergelijk OnderhandenWerk Excel met forecast">
            <Button
              size="small"
              variant="outlined"
              startIcon={<FactCheckIcon fontSize="small" />}
              onClick={() => navigate('/ohw-check')}
              sx={{ height: 32, fontSize: 12, textTransform: 'none', ml: 0.5 }}
            >
              OHW Check
            </Button>
          </Tooltip>
        </Box>
        )}
      />

      {error && <Alert severity="error" sx={{ mb: 1, py: 0 }} onClose={() => setError(null)}>{error}</Alert>}
      {importResult && <Alert severity="success" sx={{ mb: 1, py: 0 }} onClose={() => setImportResult(null)}>{importResult}</Alert>}

      {/* Summary */}
      <Box sx={{ display: 'flex', gap: 3, mb: 1, fontSize: 12, color: '#475569' }}>
        <span><strong>{rows.length}</strong> projecten</span>
        <span>Omzet: <strong>{fmt(totals.omzet)}</strong></span>
        <span>Inkoop: <strong>{fmt(totals.costs)}</strong></span>
        <span>BBI: <strong style={{ color: totals.bbi >= 0 ? '#16a34a' : '#dc2626' }}>{fmt(totals.bbi)}</strong></span>
      </Box>

      {/* Table */}
      <TableContainer sx={{ flex: 1, border: '1px solid #cbd5e1', borderRadius: 0 }}>
        <Table size="small" stickyHeader sx={{ borderCollapse: 'collapse' }}>
          <TableHead>
            {/* Grand total row */}
            <TableRow>
              <TableCell sx={{
                bgcolor: '#1e293b', color: '#fff', fontWeight: 700, fontSize: 11,
                p: '4px 8px', ...stickyCol, bgcolor: '#1e293b', zIndex: 3,
                borderRight: '2px solid #475569'
              }}>
                TOTAAL
              </TableCell>
              <TableCell sx={{ bgcolor: '#1e293b', color: '#fff', p: '4px 6px', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>
                {fmt(totals.omzet)}
              </TableCell>
              <TableCell sx={{ bgcolor: '#1e293b', color: '#fff', p: '4px 6px', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>
                {fmt(totals.costs)}
              </TableCell>
              <TableCell sx={{ bgcolor: '#1e293b', p: '4px 6px', fontSize: 11, fontWeight: 700, textAlign: 'right',
                color: totals.bbi >= 0 ? '#4ade80' : '#f87171'
              }}>
                {fmt(totals.bbi)}
              </TableCell>
              <TableCell sx={{ bgcolor: '#1e293b', color: '#94a3b8', p: '4px 6px', fontSize: 10, textAlign: 'center',
                borderRight: '2px solid #475569'
              }}>-</TableCell>
              {months.map(m => (
                <TableCell key={m.key} sx={{
                  bgcolor: m.isPast ? '#0f172a' : '#1e293b',
                  p: '4px 4px', fontSize: 10, fontWeight: 700, textAlign: 'right',
                  color: totals.monthly[m.key] >= 0 ? '#4ade80' : '#f87171',
                  borderRight: '1px solid #475569'
                }}>
                  {fmt(totals.monthly[m.key])}
                </TableCell>
              ))}
            </TableRow>
            {/* Column headers */}
            <TableRow>
              <TableCell sx={{ ...headerStyle, ...stickyCol, minWidth: 220, zIndex: 3, bgcolor: '#f1f5f9' }}>
                CSD / Klant / Project
              </TableCell>
              <TableCell sx={{ ...headerStyle, textAlign: 'right', minWidth: 75 }}>Omzet (excl. BTW)</TableCell>
              <TableCell sx={{ ...headerStyle, textAlign: 'right', minWidth: 70 }}>Inkoop</TableCell>
              <TableCell sx={{ ...headerStyle, textAlign: 'right', minWidth: 65 }}>BBI</TableCell>
              <TableCell sx={{ ...headerStyle, textAlign: 'center', minWidth: 40, borderRight: '2px solid #94a3b8' }}>%</TableCell>
              {months.map(m => (
                <TableCell key={m.key} sx={{
                  ...headerStyle, textAlign: 'center', minWidth: 55,
                  bgcolor: m.isPast ? '#e2e8f0' : '#f1f5f9'
                }}>
                  <Box sx={{ lineHeight: 1.1 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700 }}>{m.label}</Typography>
                    <Typography sx={{ fontSize: 8, opacity: 0.7 }}>'{m.year}</Typography>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {grouped.map((plGroup) => {
              const plKey = `pl:${plGroup.pl}`;
              const plCollapsed = collapsedGroups[plKey];

              return (
                <React.Fragment key={plKey}>
                  {/* Account Manager header row */}
                  <TableRow
                    onClick={() => toggleGroup(plKey)}
                    sx={{ cursor: 'pointer', '&:hover td': { bgcolor: '#dbeafe !important' } }}
                  >
                    <TableCell sx={{
                      position: 'sticky', left: 0, zIndex: 2,
                      bgcolor: '#e0e7ff', borderRight: '2px solid #cbd5e1',
                      p: '5px 8px', fontWeight: 800, fontSize: 12,
                      display: 'flex', alignItems: 'center', gap: 0.5
                    }}>
                      {plCollapsed ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
                      {plGroup.pl}
                      <span style={{ color: '#6366f1', fontWeight: 400, fontSize: 10, marginLeft: 4 }}>
                        ({plGroup.allRows.length})
                      </span>
                    </TableCell>
                    {(() => {
                      const totalOmzet = plGroup.allRows.reduce((s, r) => s + (r.omzet || 0), 0);
                      const totalCosts = plGroup.allRows.reduce((s, r) => s + (r.costs || 0), 0);
                      const totalBBI = totalOmzet - totalCosts;
                      const mt = calcMonthlyTotals(plGroup.allRows, months);
                      return (
                        <>
                          <TableCell sx={{ bgcolor: '#e0e7ff', p: '4px 6px', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>
                            {fmt(totalOmzet)}
                          </TableCell>
                          <TableCell sx={{ bgcolor: '#e0e7ff', p: '4px 6px', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>
                            {fmt(totalCosts)}
                          </TableCell>
                          <TableCell sx={{ bgcolor: '#e0e7ff', p: '4px 6px', fontSize: 11, fontWeight: 700, textAlign: 'right',
                            color: totalBBI >= 0 ? '#16a34a' : '#dc2626'
                          }}>
                            {fmt(totalBBI)}
                          </TableCell>
                          <TableCell sx={{ bgcolor: '#e0e7ff', p: '4px 6px', fontSize: 10, textAlign: 'center',
                            borderRight: '2px solid #cbd5e1'
                          }}>-</TableCell>
                          {months.map(m => (
                            <TableCell key={m.key} sx={{
                              bgcolor: m.isPast ? '#c7d2e0' : '#e0e7ff',
                              p: '4px 4px', fontSize: 10, fontWeight: 700, textAlign: 'right',
                              color: mt[m.key] >= 0 ? '#16a34a' : '#dc2626',
                              borderRight: '1px solid #c7d2fe'
                            }}>
                              {fmt(mt[m.key])}
                            </TableCell>
                          ))}
                        </>
                      );
                    })()}
                  </TableRow>

                  {/* Client groups within this PL */}
                  {!plCollapsed && plGroup.clients.map((clientGroup) => {
                    const clientKey = `${plKey}|client:${clientGroup.klant}`;
                    const clientCollapsed = collapsedGroups[clientKey];
                    const showClientHeader = plGroup.clients.length > 1 || clientGroup.projects.length > 1;

                    return (
                      <React.Fragment key={clientKey}>
                        {/* Client header row */}
                        {showClientHeader && (
                          <TableRow
                            onClick={() => toggleGroup(clientKey)}
                            sx={{ cursor: 'pointer', '&:hover td': { bgcolor: '#f0fdf4 !important' } }}
                          >
                            <TableCell sx={{
                              position: 'sticky', left: 0, zIndex: 2,
                              bgcolor: '#f0fdf4', borderRight: '2px solid #cbd5e1',
                              p: '3px 8px', pl: '24px', fontWeight: 700, fontSize: 11,
                              display: 'flex', alignItems: 'center', gap: 0.5
                            }}>
                              {clientCollapsed ? <CollapseIcon sx={{ fontSize: 14 }} /> : <ExpandIcon sx={{ fontSize: 14 }} />}
                              {clientGroup.klant}
                              <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 10, marginLeft: 4 }}>
                                ({clientGroup.projects.length})
                              </span>
                            </TableCell>
                            {(() => {
                              const totalOmzet = clientGroup.projects.reduce((s, r) => s + (r.omzet || 0), 0);
                              const totalCosts = clientGroup.projects.reduce((s, r) => s + (r.costs || 0), 0);
                              const totalBBI = totalOmzet - totalCosts;
                              const mt = calcMonthlyTotals(clientGroup.projects, months);
                              return (
                                <>
                                  <TableCell sx={{ bgcolor: '#f0fdf4', p: '3px 6px', fontSize: 11, fontWeight: 600, textAlign: 'right' }}>
                                    {fmt(totalOmzet)}
                                  </TableCell>
                                  <TableCell sx={{ bgcolor: '#f0fdf4', p: '3px 6px', fontSize: 11, fontWeight: 600, textAlign: 'right' }}>
                                    {fmt(totalCosts)}
                                  </TableCell>
                                  <TableCell sx={{ bgcolor: '#f0fdf4', p: '3px 6px', fontSize: 11, fontWeight: 600, textAlign: 'right',
                                    color: totalBBI >= 0 ? '#16a34a' : '#dc2626'
                                  }}>
                                    {fmt(totalBBI)}
                                  </TableCell>
                                  <TableCell sx={{ bgcolor: '#f0fdf4', p: '3px 6px', fontSize: 10, textAlign: 'center',
                                    borderRight: '2px solid #cbd5e1'
                                  }}>-</TableCell>
                                  {months.map(m => (
                                    <TableCell key={m.key} sx={{
                                      bgcolor: m.isPast ? '#e2e8e0' : '#f0fdf4',
                                      p: '3px 4px', fontSize: 10, fontWeight: 600, textAlign: 'right',
                                      color: mt[m.key] >= 0 ? '#16a34a' : '#dc2626',
                                      borderRight: '1px solid #d1d5db'
                                    }}>
                                      {fmt(mt[m.key])}
                                    </TableCell>
                                  ))}
                                </>
                              );
                            })()}
                          </TableRow>
                        )}

                        {/* Project rows */}
                        {!clientCollapsed && clientGroup.projects.map((row, idx) => {
                          const isEven = idx % 2 === 0;
                          const isValid = row.totalPct === 0 || row.totalPct === 100;
                          const needsAttention = row.totalPct > 0 && row.totalPct !== 100;

                          const check = planningChecks[String(row.projectnr)];
                          const planningOpen = openPlanning.has(row.id);
                          const voorstel = check?.voorstel?.pct || {};
                          const voorstelTotaal = Math.round(Object.values(voorstel).reduce((a, b) => a + (b || 0), 0));
                          const deltaKleur = (d) => (Math.abs(d) < 5 ? '#94a3b8' : Math.abs(d) < 15 ? '#d97706' : '#dc2626');
                          return (
                            <React.Fragment key={row.id}>
                            <TableRow sx={{
                              '&:hover td': { bgcolor: '#f0f9ff !important' },
                              ...(needsAttention && { borderLeft: '3px solid #ef4444' })
                            }}>
                              {/* Project name */}
                              <TableCell sx={{
                                ...cellStyle(isEven), ...stickyCol,
                                bgcolor: needsAttention ? '#fef2f2' : (isEven ? '#fafafa' : '#fff'),
                                pl: showClientHeader ? '40px' : '24px'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PlanningCheck
                                  check={check}
                                  laden={planningLaden}
                                  actief={planningOpen}
                                  onClick={() => togglePlanning(row.id)}
                                  onOvernemen={(pct) => save(row.id, { monthlyPercentages: pct })}
                                />
                                <Tooltip title={`${row.projectnr}. ${row.name} — ${row.klant}`} placement="right">
                                  <Typography
                                    onClick={(e) => { e.stopPropagation(); setEditProject(row); }}
                                    sx={{
                                      fontSize: 11, fontWeight: 500, lineHeight: 1.3,
                                      maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                      cursor: 'pointer', '&:hover': { color: '#3b82f6', textDecoration: 'underline' }
                                    }}>
                                    {row.projectnr}. {row.name}
                                  </Typography>
                                </Tooltip>
                                </Box>
                              </TableCell>

                              {/* Omzet */}
                              <TableCell sx={{ ...cellStyle(isEven), p: '1px 2px' }}>
                                <Tooltip title={row.omzet !== row.originalOmzet ? `Origineel: ${fmt(row.originalOmzet)}` : ''} placement="top">
                                  <Box>
                                    <Cell
                                      value={row.omzet}
                                      onChange={(v) => save(row.id, { projectValue: v })}
                                      type="currency"
                                    />
                                  </Box>
                                </Tooltip>
                              </TableCell>

                              {/* Inkoop */}
                              <TableCell sx={{ ...cellStyle(isEven), p: '1px 2px' }}>
                                <Cell
                                  value={row.costs}
                                  onChange={(v) => save(row.id, { purchaseCosts: v })}
                                  type="currency"
                                />
                              </TableCell>

                              {/* BBI */}
                              <TableCell sx={{
                                ...cellStyle(isEven), textAlign: 'right', fontWeight: 600,
                                color: row.bbi >= 0 ? '#16a34a' : '#dc2626', fontSize: 11
                              }}>
                                {fmt(row.bbi)}
                              </TableCell>

                              {/* Total % */}
                              <TableCell sx={{
                                ...cellStyle(isEven), textAlign: 'center',
                                fontWeight: 600, fontSize: 11,
                                borderRight: '2px solid #cbd5e1',
                                color: !isValid ? '#dc2626' : row.totalPct === 100 ? '#16a34a' : '#64748b',
                                bgcolor: !isValid ? '#fef2f2' : isEven ? '#fafafa' : '#fff'
                              }}>
                                {row.totalPct}%
                              </TableCell>

                              {/* Monthly percentages */}
                              {months.map(m => (
                                <TableCell key={m.key} sx={{
                                  ...cellStyle(isEven), p: '1px 2px', textAlign: 'center',
                                  bgcolor: m.isPast ? (isEven ? '#f0f0f0' : '#e8e8e8') : (isEven ? '#fafafa' : '#fff')
                                }}>
                                  <Cell
                                    value={row.pct[m.key] || 0}
                                    onChange={(v) => {
                                      const newPct = { ...row.pct, [m.key]: v };
                                      save(row.id, { monthlyPercentages: newPct });
                                    }}
                                  />
                                </TableCell>
                              ))}
                            </TableRow>

                            {/* Uitklaprij: voorgestelde verdeling volgens de planning, in dezelfde maandkolommen */}
                            {planningOpen && check && (
                              <TableRow sx={{ '& td': { bgcolor: '#f0f9ff', borderBottom: '2px solid #bae6fd' } }}>
                                <TableCell sx={{ ...cellStyle(isEven), ...stickyCol, bgcolor: '#f0f9ff', pl: showClientHeader ? '40px' : '24px' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontSize: 10.5, color: '#0369a1', fontWeight: 600, whiteSpace: 'nowrap' }}>↳ Planning volgens taken</Typography>
                                    <Typography sx={{ fontSize: 10, color: '#64748b', whiteSpace: 'nowrap' }}>
                                      {check.retainer ? 'doorlopend, gelijkmatig' : ['pre', 'productie', 'post'].map((f) => {
                                        const fs = check.fasen?.[f]; if (!fs) return null;
                                        const kort = f === 'pre' ? 'pre' : f === 'productie' ? 'prod' : 'post';
                                        return f === 'productie' && fs.offerte_dagen > 0 ? `${kort} ${fs.gepland_dagen}/${fs.offerte_dagen}d` : `${kort} ${fs.gepland_uren}/${fs.offerte_uren}u`;
                                      }).filter(Boolean).join(' · ')}
                                    </Typography>
                                    <Button size="small" onClick={() => setEditProject(row)} sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, px: 0.75, py: 0 }}>Details</Button>
                                    {Object.keys(voorstel).length > 0 && (
                                      <Button size="small" variant="contained" onClick={() => save(row.id, { monthlyPercentages: voorstel })} sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, px: 0.75, py: 0 }}>Overnemen</Button>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ ...cellStyle(isEven), bgcolor: '#f0f9ff' }} />
                                <TableCell sx={{ ...cellStyle(isEven), bgcolor: '#f0f9ff' }} />
                                <TableCell sx={{ ...cellStyle(isEven), bgcolor: '#f0f9ff', textAlign: 'right', fontSize: 10, color: '#b45309' }}>
                                  {check.voorstel?.ongepland_pct > 0 ? `${check.voorstel.ongepland_pct}% niet gepland` : ''}
                                </TableCell>
                                <TableCell sx={{ ...cellStyle(isEven), bgcolor: '#f0f9ff', textAlign: 'center', fontSize: 11, fontWeight: 600, borderRight: '2px solid #cbd5e1', color: voorstelTotaal >= 99 ? '#16a34a' : '#b45309' }}>
                                  {Object.keys(voorstel).length ? `${voorstelTotaal}%` : '–'}
                                </TableCell>
                                {months.map((m) => {
                                  const v = voorstel[m.key] || 0;
                                  const d = v - (row.pct[m.key] || 0);
                                  return (
                                    <TableCell key={m.key} sx={{ ...cellStyle(isEven), bgcolor: '#f0f9ff', textAlign: 'center', p: '1px 2px', fontSize: 11 }}>
                                      {v ? (
                                        <Tooltip title={`Planning ${v}% · nu ${row.pct[m.key] || 0}% · verschil ${d > 0 ? '+' : ''}${Math.round(d * 10) / 10} pp`}>
                                          <Box component="span" sx={{ fontWeight: 600, color: deltaKleur(d) }}>{v}</Box>
                                        </Tooltip>
                                      ) : (row.pct[m.key] ? <Box component="span" sx={{ color: deltaKleur(d), fontSize: 10 }}>0</Box> : <Box component="span" sx={{ color: '#cbd5e1' }}>-</Box>)}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            )}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Keyboard hints */}
      <Box sx={{ mt: 1, fontSize: 10, color: '#94a3b8' }}>
        Tab/Enter: volgende cel · ↑↓: navigeer rijen · Klik op projectnaam voor details · Automatisch opslaan
      </Box>

      {/* Project Edit Dialog */}
      <Dialog open={!!editProject} onClose={() => setEditProject(null)} maxWidth={editProject && planningChecks[String(editProject.projectnr)]?.signaal && planningChecks[String(editProject.projectnr)].signaal !== 'geen' ? 'md' : 'xs'} fullWidth>
        {editProject && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {editProject.projectnr}. {editProject.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {editProject.klant}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setEditProject(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Account Manager</InputLabel>
                  <Select
                    value={dialogPL}
                    label="Account Manager"
                    onChange={(e) => setDialogPL(e.target.value)}
                  >
                    {activeEmployees.map(emp => (
                      <MenuItem key={emp} value={emp}>{emp}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Omzet (excl. BTW)"
                  type="number"
                  size="small"
                  fullWidth
                  value={dialogOmzet}
                  onChange={(e) => setDialogOmzet(parseFloat(e.target.value) || 0)}
                  InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                />
                <TextField
                  label="Inkoop"
                  type="number"
                  size="small"
                  fullWidth
                  value={dialogCosts}
                  onChange={(e) => setDialogCosts(parseFloat(e.target.value) || 0)}
                  InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, py: 1.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={600}>BBI</Typography>
                  <Typography variant="body2" fontWeight={700}
                    sx={{ color: (dialogOmzet - dialogCosts) >= 0 ? '#16a34a' : '#dc2626' }}>
                    {fmt(dialogOmzet - dialogCosts)}
                  </Typography>
                </Box>
                {/* Planning-check: taken vs offerteregels per fase, en in welke maand de BBI volgens de planning valt */}
                <Box sx={{ borderTop: '1px solid #e2e8f0', pt: 1.5 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Planning versus offerte</Typography>
                  <PlanningCheckDetail
                    compact
                    check={planningChecks[String(editProject.projectnr)]}
                    onOvernemen={(pct) => { save(editProject.id, { monthlyPercentages: pct }); setEditProject(null); }}
                  />
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setEditProject(null)}>Annuleren</Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => {
                  // Update PL in allProjects if changed (so grouping updates)
                  if (dialogPL !== editProject.pl) {
                    setAllProjects(prev => prev.map(p =>
                      p.id === editProject.id ? { ...p, pl: dialogPL } : p
                    ));
                  }
                  // Save via existing save function
                  save(editProject.id, {
                    projectValue: dialogOmzet,
                    purchaseCosts: dialogCosts
                  });
                  // Patch pendingSaves with updated pl
                  if (dialogPL !== editProject.pl && pendingSaves.current[editProject.id]) {
                    pendingSaves.current[editProject.id].project = {
                      ...pendingSaves.current[editProject.id].project,
                      pl: dialogPL
                    };
                  }
                  setEditProject(null);
                }}
              >
                Opslaan
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
